import { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  LogOut,
  User,
  Plug,
  Home,
  Zap,
  Rocket,
  Target,
  BarChart2,
  Megaphone,
  CreditCard,
  HelpCircle,
  Lock,
  Settings,
  LayoutDashboard,
  CalendarRange,
  CalendarDays,
} from 'lucide-react';
import { AuthContext } from 'components/context/AuthContext';
import { useHelpCenter } from 'components/context/HelpCenterContext';
import { ModalsContext } from 'components/context/ModalsContext';
import { useGoogleAds } from '../hooks/useGoogleAds';
import { useMetaAds } from '../hooks/useMetaAds';
import { GoogleIcon, MetaIcon } from 'components';
import CompactConnectionBadge from './BrandSelector/CompactConnectionBadge';
import MobileBrandSelectorSheet from './BrandSelector/MobileBrandSelectorSheet';
import { useQueryClient } from '@tanstack/react-query';
import { logApi } from '../api';
import { invalidateObjetivosQueries, useObjetivosQuery } from '../queries/objetivos';
import { ChevronDown } from 'lucide-react';

const { createLog } = logApi;

const CHROME_LOCK_TOOLTIP = 'Completá el paso actual para continuar';

const ANALYTICS_SUBMENU = [
  { label: 'Dashboard', path: '/analytics20', description: 'Analytics' },
  { label: 'Reporting', path: '/reporting', description: 'Reporting' }
];

const ADS_SUBMENU = [
  { label: 'Edad de anuncios', path: '/ads-age', description: 'Meta' },
  { label: 'Performance anuncios', path: '/ads-performance', description: 'Performance' },
];

const Sidebar = ({ onExpandedChange, onNavigate, showMobileFooterMenu = true, onSidebarStateChange }) => {
  const location = useLocation();
  const defaultNavigate = useNavigate();
  const navigate = onNavigate || defaultNavigate;
  const { auth, logout, hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { openHelpCenter } = useHelpCenter();
  const { openPlansModal, openConnectionsModal, isMandatoryAdsObjectivesChromeLocked } =
    useContext(ModalsContext);
  const chromeLocked = Boolean(isMandatoryAdsObjectivesChromeLocked);
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const queryClient = useQueryClient();
  const activeBrandId = auth?.user?.active_brand_id;
  const activeBrand = auth?.user?.brands?.find((b) => b.id === activeBrandId);
  const googleConnected = activeBrand ? Boolean(activeBrand.google_ads_connected) : hasGoogleAdsConnected;
  const metaConnected = activeBrand ? Boolean(activeBrand.meta_connected) : hasMetaConnected;

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const { data: objectives = [], isPending: loadingObjectives } = useObjetivosQuery(activeBrandId);
  const [isBrandSelectorOpen, setIsBrandSelectorOpen] = useState(false);
  const [screenSize, setScreenSize] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true
  });
  const [openSubmenuLabel, setOpenSubmenuLabel] = useState(null);
  const profileMenuRef = useRef(null);
  const sidebarRef = useRef(null);
  const userCampaignsVisibility = auth?.user?.show_campaigns_module;
  const activeBrandCampaignsVisibility = auth?.user?.active_brand?.show_campaigns_module;
  // Por defecto mostramos "Campañas" en el sidebar.
  // Solo lo ocultamos si el usuario o la marca lo desactivan explícitamente.
  const showCampaignsModule = userCampaignsVisibility !== false && activeBrandCampaignsVisibility !== false;

  useEffect(() => {
    const handler = () => {
      if (activeBrandId != null) invalidateObjetivosQueries(queryClient, activeBrandId);
    };
    window.addEventListener('objectives-created', handler);
    return () => window.removeEventListener('objectives-created', handler);
  }, [activeBrandId, queryClient]);

  const isOptimizerLocked = !loadingObjectives && Array.isArray(objectives) && objectives.length === 0;

  // Notificar al layout principal el ancho fijo del sidebar
  useEffect(() => {
    if (onSidebarStateChange) {
      onSidebarStateChange({
        isCompressed: true,
        width: 96 // Ancho fijo para íconos + etiquetas (w-24 = 96px)
      });
    }
  }, [onSidebarStateChange]);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const sz = {
        isMobile: width < 768,
        isTablet: width >= 768 && width < 1024,
        isDesktop: width >= 1024
      };
      setScreenSize(sz);
      if (sz.isDesktop) onExpandedChange?.(true);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onExpandedChange]);

  useEffect(() => {
    if (screenSize.isMobile || screenSize.isTablet) {
      setShowProfileMenu(false);
    }
  }, [location.pathname, screenSize]);

  // Handle click outside profile menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    
    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileMenu]);

  const handleNavigation = useCallback((path, disabled, label, event) => {
    if (disabled) return;
    
    // Detectar si se presionó Ctrl/Cmd o clic medio para abrir en nueva pestaña
    const isMiddleClick = event?.button === 1;
    const isCtrlClick = event?.ctrlKey || event?.metaKey;
    
    if (isMiddleClick || isCtrlClick) {
      event?.preventDefault();
      // Log the page visit with the menu item label
      createLog({
        level: 'info',
        message: `Click en ${label} en sidebar (nueva pestaña)`,
      });
      
      // Abrir en nueva pestaña usando window.open con URL completa
      const fullUrl = window.location.origin + path;
      const newWindow = window.open(fullUrl, '_blank');
      if (newWindow) {
        newWindow.opener = null; // Seguridad: evitar que la nueva ventana acceda a window.opener
      }
      return;
    }
    
    // Navegación normal
    event?.preventDefault();
    // Log the page visit with the menu item label
    createLog({
      level: 'info',
      message: `Click en ${label} en sidebar`,
    });
    
    navigate(path);
  }, [navigate]);

  const handleLogout = async () => {
    createLog({
      level: 'info',
      message: 'Click en "Cerrar sesión" en sidebar'
    });
    
    setShowProfileMenu(false);
    await logout();
    sessionStorage.removeItem('active_session_id');
    sessionStorage.removeItem('recent_sessions');
    navigate('/login');
  };

  const hasBrandPanel = Boolean(auth?.user?.has_brand_panel);
  const isAdmin = Boolean(auth?.user?.is_admin);
  // Growth partner: acceso restringido a solo el Panel (+ Perfil para invitar).
  const isGrowthPartner = Boolean(auth?.user?.is_growth_partner);
  // Admin ve "Panel" (todas las marcas); el cliente ve "Mi panel" (su marca).
  const panelItem = useMemo(
    () =>
      isAdmin
        ? { icon: LayoutDashboard, label: 'Panel', path: '/admin/panel' }
        : hasBrandPanel
          ? { icon: LayoutDashboard, label: 'Mi panel', path: '/mi-panel' }
          : null,
    [isAdmin, hasBrandPanel]
  );

  const menuItemsBase = useMemo(
    () => {
      if (isGrowthPartner) {
        return [
          { icon: LayoutDashboard, label: 'Panel', path: '/mi-panel' },
          { icon: Settings, label: 'Mi configuración', path: '/profile' },
          { icon: User, label: 'Usuario', path: '/profile/users' },
        ];
      }
      return [
        { icon: Home, label: 'Inicio', path: '/home', dataTour: 'nav-inicio' },
        ...(panelItem ? [panelItem] : []),
        ...(isAdmin
          ? [
              { icon: CalendarRange, label: 'Media planning', path: '/admin/media-planning' },
              { icon: CalendarDays, label: 'Calendario de promos', path: '/admin/promo-calendar' },
            ]
          : []),
        { icon: Rocket, label: 'Optimizaciones', path: '/optimizador', dataTour: 'nav-optimizaciones' },
        { icon: Target, label: 'Objetivos', path: '/objetivos', dataTour: 'nav-objetivos' },
        ...(showCampaignsModule ? [{ icon: Zap, label: 'Campañas', path: '/campaigns', dataTour: 'nav-campañas' }] : []),
        {
          icon: Megaphone,
          label: 'Anuncios',
          path: '/ads-age',
          dataTour: 'nav-anuncios',
          submenu: ADS_SUBMENU,
        },
        {
          icon: BarChart2,
          label: 'Analytics',
          path: '/analytics20',
          dataTour: 'nav-analytics',
          submenu: ANALYTICS_SUBMENU
        }
      ];
    },
    [showCampaignsModule, panelItem, isAdmin, isGrowthPartner]
  );

  const menuItemsWithLock = useMemo(
    () => menuItemsBase.map((item) => ({ ...item, disabled: Boolean(item.disabled) || chromeLocked })),
    [menuItemsBase, chromeLocked]
  );

  const profileItems = [
    {
      icon: LogOut,
      label: 'Cerrar sesión',
      onClick: handleLogout,
    },
  ];

  const bottomItems = isGrowthPartner
    ? []
    : [
        {
          icon: HelpCircle,
          label: 'Support',
          action: 'helpCenter',
          tooltip: 'Soporte',
        },
        {
          icon: CreditCard,
          label: 'Billing',
          action: 'openPlans',
          tooltip: 'Suscripción',
        },
        {
          icon: Settings,
          label: 'Profile',
          action: 'openProfile',
          tooltip: 'Configuración',
        },
      ];

  const mobileMenuBase = useMemo(
    () => {
      if (isGrowthPartner) {
        return [
          { icon: LayoutDashboard, label: 'Panel', path: '/mi-panel' },
          { icon: Settings, label: 'Mi configuración', path: '/profile' },
          { icon: User, label: 'Usuario', path: '/profile/users' },
        ];
      }
      return [
        { icon: Home, label: 'Inicio', path: '/home', dataTour: 'nav-inicio' },
        ...(panelItem ? [panelItem] : []),
        ...(isAdmin
          ? [
              { icon: CalendarRange, label: 'Media planning', path: '/admin/media-planning' },
              { icon: CalendarDays, label: 'Calendario de promos', path: '/admin/promo-calendar' },
            ]
          : []),
        { icon: Rocket, label: 'Optimizaciones', path: '/optimizador', dataTour: 'nav-optimizaciones' },
        { icon: Target, label: 'Objetivos', path: '/objetivos', dataTour: 'nav-objetivos' },
        ...(showCampaignsModule ? [{ icon: Zap, label: 'Campañas', path: '/campaigns' }] : []),
        { icon: Megaphone, label: 'Anuncios', path: '/ads-age', dataTour: 'nav-anuncios' },
        { icon: BarChart2, label: 'Analytics', path: '/analytics20', dataTour: 'nav-analytics' },
        { icon: User, label: 'Mi perfil', path: '/profile', disabled: false }
      ];
    },
    [showCampaignsModule, panelItem, isAdmin, isGrowthPartner]
  );

  const mobileMenuItemsWithLock = useMemo(
    () => mobileMenuBase.map((item) => ({ ...item, disabled: Boolean(item.disabled) || chromeLocked })),
    [mobileMenuBase, chromeLocked]
  );

  if (screenSize.isMobile || screenSize.isTablet) {
    return (
      <>
        {/* Mobile Footer Menu: Dashboard - Optimizaciones - Objetivos - Campañas - Analytics - Perfil */}
        <footer className={`fixed bottom-0 left-0 right-0 z-[9998] bg-white border-t border-gray-200 ${screenSize.isDesktop ? 'hidden' : 'block'}`}>
          <div className="flex justify-around items-center py-2">
            {mobileMenuItemsWithLock.map(item => {
              const isActive = item.path === '/profile'
                ? location.pathname.startsWith('/profile')
                : location.pathname === item.path;
              return (
                <a
                  key={item.path || item.label}
                  href={item.disabled || item.onClick ? undefined : item.path}
                  {...(item.dataTour && { 'data-tour': item.dataTour })}
                  aria-label={
                    chromeLocked && item.disabled
                      ? `${item.label}. ${CHROME_LOCK_TOOLTIP}`
                      : undefined
                  }
                  title={chromeLocked && item.disabled ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={(e) => {
                    if (item.onClick) {
                      e.preventDefault();
                      item.onClick();
                    } else {
                      handleNavigation(item.path, item.disabled, item.label, e);
                    }
                  }}
                  onMouseDown={(e) => {
                    // Detectar clic medio (button === 1)
                    if (e.button === 1 && !item.disabled && !item.onClick) {
                      e.preventDefault();
                      handleNavigation(item.path, item.disabled, item.label, e);
                    }
                  }}
                  className={`
                    flex flex-col items-center p-2 rounded-lg transition-colors min-w-0 flex-1
                    ${item.disabled
                      ? 'opacity-50 cursor-not-allowed pointer-events-none'
                      : isActive
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:bg-gray-100'
                    }
                  `}
                >
                  <item.icon size={20} />
                  <span className="text-xs font-medium mt-1 truncate w-full text-center">
                    {item.label}
                  </span>
                </a>
              );
            })}
          </div>
        </footer>
      </>
    );
  }

  return (
    <>
      {/* Desktop Vertical Sidebar */}
      {screenSize.isDesktop && (
        <aside 
          ref={sidebarRef}
          className="backdrop-blur-xl bg-gradient-to-br from-slate-800/80 via-slate-900/80 to-indigo-900/80 border-r border-white/20 shadow-2xl fixed left-0 z-[99999] w-24"
          style={{ 
            zIndex: 99999,
            top: 0,
            left: 0,
            height: '100vh',
            margin: 0,
            padding: 0,
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.3)'
          }}
        >
          <div className="flex flex-col h-full" style={{ margin: 0, padding: 0 }}>
            {/* Logo */}
            <div className="flex items-center justify-center h-20 px-2">
              <div
                role="button"
                tabIndex={chromeLocked ? -1 : 0}
                aria-disabled={chromeLocked}
                onClick={() => {
                  if (chromeLocked) return;
                  navigate('/home');
                }}
                onKeyDown={(e) => {
                  if (chromeLocked) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/home');
                  }
                }}
                className={`transition-transform duration-300 ease-out ${
                  chromeLocked
                    ? 'opacity-40 cursor-not-allowed pointer-events-none'
                    : 'cursor-pointer hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 rounded-md'
                }`}
              >
                <svg width="32" height="32" viewBox="0 0 174 175" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M134.26 37.04C134.26 90.99 90.37 134.89 36.41 134.89V174.46C112.18 174.46 173.82 112.82 173.82 37.05H134.26V37.04Z" fill="#0F31F7"/>
                  <path d="M19.63 39.26C30.4713 39.26 39.26 30.4713 39.26 19.63C39.26 8.78865 30.4713 0 19.63 0C8.78865 0 0 8.78865 0 19.63C0 30.4713 8.78865 39.26 19.63 39.26Z" fill="#0F31F7"/>
                  <path d="M86.12 39.26C96.9613 39.26 105.75 30.4713 105.75 19.63C105.75 8.78865 96.9613 0 86.12 0C75.2787 0 66.42 8.78865 66.49 19.63C66.49 30.4713 75.2787 39.26 86.12 39.26Z" fill="#0F31F7"/>
                </svg>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-6 px-1">
              {menuItemsWithLock.map(item => {
                const hasSubmenu = Array.isArray(item.submenu);
                const isActive = hasSubmenu
                  ? item.submenu.some(sub => location.pathname === sub.path)
                  : location.pathname === item.path;
                const isOptimizerItem = item.path === '/optimizador';

                // Item Optimizaciones bloqueado: mantiene estilo bloqueado pero navega al optimizador
                if (isOptimizerItem && isOptimizerLocked && !chromeLocked) {
                  return (
                    <div key={item.path || item.label} className="relative group mb-2">
                      <a
                        href={item.path}
                        onClick={(e) => handleNavigation(item.path, item.disabled, item.label, e)}
                        onMouseDown={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            handleNavigation(item.path, item.disabled, item.label, e);
                          }
                        }}
                        className={`flex flex-col items-center justify-center w-full px-2 py-2 rounded-lg transition-all duration-300 ease-out relative cursor-pointer ${
                          isActive
                            ? 'bg-amber-500/25 border-2 border-amber-400/60 shadow-md shadow-amber-500/20'
                            : 'bg-amber-900/35 border-2 border-amber-500/50 hover:bg-amber-500/30 hover:border-amber-400/60'
                        }`}
                        {...(item.dataTour && { 'data-tour': item.dataTour })}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <Lock size={22} className="text-amber-300 flex-shrink-0" />
                          <span className="text-[10px] font-bold text-center leading-tight text-amber-200/95">
                            {item.label}
                          </span>
                          <span className="text-[8px] font-medium text-amber-300/80 uppercase tracking-wide">
                            Bloqueado
                          </span>
                        </div>
                      </a>
                    </div>
                  );
                }

                if (hasSubmenu) {
                  if (item.disabled) {
                    return (
                      <div key={item.path || item.label} className="relative group mb-2">
                        <div
                          className="flex flex-col items-center justify-center w-full px-2 py-2 rounded-lg opacity-50 cursor-not-allowed pointer-events-none border border-transparent"
                          aria-disabled="true"
                          title={CHROME_LOCK_TOOLTIP}
                        >
                          <item.icon size={20} className="flex-shrink-0 mb-1 text-white/50" />
                          <span className="text-[10px] font-bold text-center leading-tight text-white/50">
                            {item.label}
                          </span>
                        </div>
                      </div>
                    );
                  }
                  const submenuVisible = openSubmenuLabel === item.label;
                  return (
                    <div
                      key={item.path || item.label}
                      className="relative group mb-2"
                      onMouseEnter={() => setOpenSubmenuLabel(item.label)}
                      onMouseLeave={() => setOpenSubmenuLabel(null)}
                      onFocus={() => setOpenSubmenuLabel(item.label)}
                      onBlur={(event) => {
                        if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) {
                          return;
                        }
                        setOpenSubmenuLabel(null);
                      }}
                    >
                      <a
                        href={item.path}
                        aria-haspopup="menu"
                      aria-expanded={submenuVisible}
                        {...(item.dataTour && { 'data-tour': item.dataTour })}
                        onClick={(e) => {
                          e.preventDefault();
                          handleNavigation(item.path, item.disabled, item.label, e);
                        }}
                        onMouseDown={(e) => {
                          if (e.button === 1) {
                            e.preventDefault();
                            handleNavigation(item.path, item.disabled, item.label, e);
                          }
                        }}
                        className={`
                          flex flex-col items-center justify-center w-full px-2 py-2 rounded-lg transition-all duration-300 ease-out relative
                          ${isActive
                            ? 'bg-white/20 backdrop-blur-md border border-white/30 shadow-lg shadow-blue-500/20'
                            : 'hover:bg-white/10 hover:backdrop-blur-sm border border-transparent hover:border-white/20'
                          }
                        `}
                        style={isActive ? {
                          boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.15)'
                        } : {}}
                      >
                        <item.icon
                          size={20}
                          className={`flex-shrink-0 mb-1 ${isActive ? 'text-white' : 'text-white/95'}`}
                        />
                        <span className={`text-[10px] font-bold text-center leading-tight ${isActive ? 'text-white' : 'text-white/90'}`}>
                          {item.label}
                        </span>
                      </a>

                      <div
                        className={`
                          absolute left-full top-2 translate-y-0 w-56 transition-all duration-200 rounded-2xl border border-white/15 bg-slate-900/95 p-3 shadow-2xl backdrop-blur-xl text-white text-sm
                          ${submenuVisible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
                        `}
                        style={{ left: 'calc(100% + 2px)' }}
                        tabIndex={-1}
                      >
                        <div className="flex flex-col gap-1">
                          {item.submenu.map(sub => (
                            <button
                              key={sub.path}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigation(sub.path, item.disabled, sub.label, e);
                              }}
                              className="flex flex-col text-left rounded-xl px-3 py-2 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                            >
                              <span className="font-semibold text-white">{sub.label}</span>
                              <span className="text-[11px] text-white/60">{sub.description}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={item.path || item.label} className="relative group mb-2">
                    <a
                      href={item.disabled ? undefined : item.path}
                      {...(item.dataTour && { 'data-tour': item.dataTour })}
                      onClick={(e) => {
                        if (item.onClick) {
                          e.preventDefault();
                          item.onClick();
                        } else {
                          handleNavigation(item.path, item.disabled, item.label, e);
                        }
                      }}
                      onMouseDown={(e) => {
                        if (e.button === 1 && !item.disabled) {
                          e.preventDefault();
                          handleNavigation(item.path, item.disabled, item.label, e);
                        }
                      }}
                      className={`
                        flex flex-col items-center justify-center w-full px-2 py-2 rounded-lg transition-all duration-300 ease-out relative
                        ${item.disabled
                          ? 'opacity-50 cursor-not-allowed pointer-events-none'
                          : isActive
                          ? 'bg-white/20 backdrop-blur-md border border-white/30 shadow-lg shadow-blue-500/20'
                          : 'hover:bg-white/10 hover:backdrop-blur-sm border border-transparent hover:border-white/20'
                        }
                      `}
                      style={isActive ? {
                        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.15)'
                      } : {}}
                    >
                      <item.icon
                        size={20}
                        className={`flex-shrink-0 mb-1 ${
                          item.disabled
                            ? 'text-white/50'
                            : isActive
                            ? 'text-white'
                            : 'text-white/95'
                        }`}
                      />
                      <span className={`text-[10px] font-bold text-center leading-tight ${
                        item.disabled
                          ? 'text-white/50'
                          : isActive
                          ? 'text-white'
                          : 'text-white/90'
                      }`}>
                        {item.label}
                      </span>
                    </a>
                  </div>
                );
              })}
            </nav>

            {/* Conexiones Icon */}
            <div className="px-1 pb-1">
              <div className="relative group">
                <button
                  type="button"
                  disabled={chromeLocked}
                  aria-disabled={chromeLocked}
                  tabIndex={chromeLocked ? -1 : 0}
                  onClick={() => {
                    if (chromeLocked) return;
                    createLog({
                      level: 'info',
                      message: 'Click en Conexiones en sidebar'
                    });
                    openConnectionsModal?.();
                  }}
                  className={`flex flex-col items-center justify-center w-full px-2 py-1 rounded-lg transition-all duration-300 ease-out border border-transparent relative focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
                    chromeLocked
                      ? 'opacity-50 cursor-not-allowed pointer-events-none'
                      : 'hover:bg-white/10 hover:backdrop-blur-sm hover:border-white/20'
                  }`}
                >
                  <Plug
                    size={20}
                    className="flex-shrink-0 text-white/95"
                  />
                  <div className="absolute left-full ml-2 px-3 py-1.5 bg-slate-900/95 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-white/10">
                    {chromeLocked ? CHROME_LOCK_TOOLTIP : 'Conexiones'}
                  </div>
                </button>
              </div>
            </div>

            {/* Bottom Items: Soporte & Suscripción */}
            <div className="px-1 pb-1">
              {bottomItems.map(item => (
                <div key={item.label} className="relative group mb-1">
                  <button
                    type="button"
                    disabled={chromeLocked}
                    aria-disabled={chromeLocked}
                    tabIndex={chromeLocked ? -1 : 0}
                    onClick={() => {
                      if (chromeLocked) return;
                      if (item.action === 'helpCenter') {
                        createLog({
                          level: 'info',
                          message: `Click en ${item.label} en sidebar (Centro de ayuda)`
                        });
                        openHelpCenter();
                      } else if (item.action === 'openPlans') {
                        createLog({
                          level: 'info',
                          message: `Click en ${item.label} en sidebar (Planes de suscripción)`
                        });
                        openPlansModal?.();
                      } else if (item.action === 'openProfile') {
                        createLog({
                          level: 'info',
                          message: 'Click en Configuración en sidebar'
                        });
                        navigate('/profile');
                      } else {
                        createLog({
                          level: 'info',
                          message: `Click en ${item.label} en sidebar`
                        });
                        navigate(item.path);
                      }
                    }}
                    className={`flex flex-col items-center justify-center w-full px-2 py-1 rounded-lg transition-all duration-300 ease-out border border-transparent relative focus-visible:ring-2 focus-visible:ring-white/30 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
                      chromeLocked
                        ? 'opacity-50 cursor-not-allowed pointer-events-none'
                        : 'hover:bg-white/10 hover:backdrop-blur-sm hover:border-white/20'
                    }`}
                  >
                    <item.icon
                      size={20}
                      className="flex-shrink-0 text-white/95"
                    />
                    <div className="absolute left-full ml-2 px-3 py-1.5 bg-slate-900/95 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none border border-white/10">
                      {chromeLocked ? CHROME_LOCK_TOOLTIP : item.tooltip}
                    </div>
                  </button>
                </div>
              ))}
            </div>

            {/* Profile Menu */}
            <div className="relative px-2 py-3" ref={profileMenuRef}>
              <button
                type="button"
                data-testid="sidebar-profile-menu-button"
                aria-haspopup="menu"
                aria-expanded={showProfileMenu}
                aria-label="Menú de cuenta"
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex flex-col items-center justify-center w-full px-2 py-2 rounded-lg hover:bg-white/10 hover:backdrop-blur-sm transition-all duration-300 border border-transparent hover:border-white/20"
              >
                <div className="relative flex-shrink-0 mb-1">
                  <div 
                    className="h-10 w-10 rounded-full backdrop-blur-md bg-white/15 flex items-center justify-center border-2 border-white/30 transition-all duration-300 hover:scale-110 hover:border-white/50 relative overflow-hidden"
                    style={{
                      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 4px 12px rgba(0, 0, 0, 0.2), 0 0 20px rgba(59, 130, 246, 0.2)'
                    }}
                  >
                    {/* Efecto de brillo superior */}
                    <div 
                      className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/40 to-transparent rounded-t-full pointer-events-none"
                    />
                    {auth.user?.profile_image ? (
                      <img 
                        key={`profile-image-${auth.user.profile_image}`} 
                        src={auth.user.profile_image} 
                        alt="Profile" 
                        className="rounded-full h-10 w-10 flex-shrink-0 object-cover relative z-10" 
                      />
                    ) : (
                      <span className="text-sm font-bold text-white flex-shrink-0 relative z-10">
                        {auth.user?.first_name?.[0]?.toUpperCase() || 'U'}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] font-bold text-center leading-tight text-white/90">
                  {auth.user?.name || auth.user?.first_name || 'Usuario'}
                </span>
              </button>

              {showProfileMenu && (
                <div
                  className="absolute bottom-full left-0 mb-2 ml-2 w-64 backdrop-blur-xl bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 rounded-lg shadow-2xl overflow-hidden border border-white/20 z-50"
                  style={{
                    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4), 0 0 40px rgba(59, 130, 246, 0.1)'
                  }}
                >
                  {profileItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => { 
                        item.onClick(); 
                        setShowProfileMenu(false); 
                      }}
                      className="flex items-center w-full px-4 py-3 text-white/90 hover:bg-white/10 hover:text-white transition-all duration-300 border-b border-white/5 last:border-b-0"
                    >
                      <item.icon size={16} className="mr-3 flex-shrink-0" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Mobile Footer Menu */}
      {!screenSize.isDesktop && showMobileFooterMenu && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200">
          {/* Brand Selector Section */}
          {auth.user?.brands?.find(b => b.id === auth.user?.active_brand_id) && (
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {/* Connection Badges */}
                <CompactConnectionBadge
                  connected={googleConnected}
                  icon={GoogleIcon}
                  label="Google Ads"
                  disabled={chromeLocked}
                  disabledReason={chromeLocked ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={googleConnected ? openConnectionsModal : () => connectGoogleAds(false)}
                />
                <CompactConnectionBadge
                  connected={metaConnected}
                  icon={MetaIcon}
                  label="Meta Ads"
                  disabled={chromeLocked}
                  disabledReason={chromeLocked ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={metaConnected ? openConnectionsModal : () => startMetaOAuth(false)}
                />
              </div>
              {/* Brand Selector Button */}
              <button
                type="button"
                disabled={chromeLocked}
                aria-disabled={chromeLocked}
                onClick={() => {
                  if (chromeLocked) return;
                  setIsBrandSelectorOpen(true);
                }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border border-gray-300 text-gray-900 text-xs font-medium transition-all touch-manipulation flex-shrink-0 ${
                  chromeLocked
                    ? 'opacity-50 cursor-not-allowed pointer-events-none bg-gray-100'
                    : 'bg-white/10 hover:bg-gray-100 active:scale-95'
                }`}
                title={chromeLocked ? CHROME_LOCK_TOOLTIP : 'Cambiar marca'}
              >
                <span className="truncate max-w-[60px]">{auth.user?.brands?.find(b => b.id === auth.user?.active_brand_id)?.name || 'Marca'}</span>
                <ChevronDown size={12} className="flex-shrink-0" />
              </button>
            </div>
          )}
          <div className="flex justify-around items-center py-2">
            {mobileMenuItemsWithLock.map(item => {
              const mobileLabel = item.label;
              const isActive = item.path === '/profile'
                ? location.pathname.startsWith('/profile')
                : location.pathname === item.path;
              const baseClassName = `flex flex-col items-center p-2 rounded-lg transition-colors min-w-0 flex-1 ${
                item.disabled
                  ? 'opacity-50 cursor-not-allowed pointer-events-none'
                  : isActive
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-100'
              }`;
              const content = (
                <>
                  <item.icon size={20} />
                  <span className="text-xs font-medium mt-1 truncate w-full text-center">
                    {mobileLabel}
                  </span>
                </>
              );
              if (item.disabled || item.onClick) {
                return (
                  <button
                    key={item.path || item.label}
                    type="button"
                    disabled={item.disabled}
                    aria-label={
                      chromeLocked && item.disabled
                        ? `${mobileLabel}. ${CHROME_LOCK_TOOLTIP}`
                        : mobileLabel
                    }
                    title={chromeLocked && item.disabled ? CHROME_LOCK_TOOLTIP : undefined}
                    onClick={(e) => {
                      if (item.onClick) item.onClick();
                      else handleNavigation(item.path, item.disabled, item.label, e);
                    }}
                    className={baseClassName}
                  >
                    {content}
                  </button>
                );
              }
              return (
                <a
                  key={item.path || item.label}
                  href={item.path}
                  aria-label={
                    chromeLocked && item.disabled
                      ? `${mobileLabel}. ${CHROME_LOCK_TOOLTIP}`
                      : undefined
                  }
                  title={chromeLocked && item.disabled ? CHROME_LOCK_TOOLTIP : undefined}
                  onClick={(e) => handleNavigation(item.path, item.disabled, item.label, e)}
                  onMouseDown={(e) => {
                    if (e.button === 1) {
                      e.preventDefault();
                      handleNavigation(item.path, item.disabled, item.label, e);
                    }
                  }}
                  className={baseClassName}
                >
                  {content}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Brand Selector Sheet */}
      {!screenSize.isDesktop && (
        <MobileBrandSelectorSheet
          isOpen={isBrandSelectorOpen}
          onClose={() => setIsBrandSelectorOpen(false)}
        />
      )}
    </>
  );
};

Sidebar.propTypes = {
  onExpandedChange: PropTypes.func,
  onNavigate: PropTypes.func,
  showMobileFooterMenu: PropTypes.bool,
  onSidebarStateChange: PropTypes.func
};

export default Sidebar;